/**
 * Genre normalization for consistent display and filtering across Radio.
 * Maps known variants to canonical genre names and provides deduplication.
 *
 * A show whose genre is the importer's default ('Mix') has no genre, and is
 * shown as a show with no genre is (TEM-1529).
 */

/**
 * The genre the Mixcloud importer writes for a show with no tags
 * (`scripts/mixcloud-to-tempest.py`). It means UNKNOWN, not a genre called
 * Mix. The backend reads it the same way (`radioGenre.ts`); CRA cannot import
 * from outside src, so a test in each package holds both to the importer.
 */
export const IMPORTER_DEFAULT_GENRE = 'Mix';

/** Is `genre` the importer's default, i.e. no genre? Exact, bar whitespace:
 *  an uploader's own 'mix' tag is theirs. */
export function isImporterDefault(genre: string): boolean {
  return genre.trim() === IMPORTER_DEFAULT_GENRE;
}

/** The genre a show really carries, or undefined when it has none: blank, or
 *  the importer's default. */
export function taggedGenre(genre?: string): string | undefined {
  if (!genre || !genre.trim() || isImporterDefault(genre)) return undefined;
  return genre;
}

export const CANONICAL_GENRES = [
  'House',
  'Techno',
  'Drum & Bass',
  'Jazz',
  'Ambient',
  'Disco',
  'Soul',
  'Hip-Hop',
  'Electronic',
  'Funk',
  'Blues',
  'Reggae',
  'Downtempo',
  'Breaks',
] as const;

// Build alias map from [canonical, ...variants] groups
// eslint-disable-next-line sonarjs/no-duplicate-string
const GENRE_ALIASES: Record<string, string> = Object.fromEntries(
  ([
    ['Drum & Bass', 'drum and bass', 'drum & bass', 'drum n bass', 'dnb', 'd&b', "d'n'b"],
    ['Hip-Hop', 'hip hop', 'hip-hop', 'hiphop'],
    ['Electronic', 'electronic', 'electronica', 'edm', 'dubstep'],
    ['House', 'house', 'deep house', 'tech house', 'acid house', 'progressive house'],
    ['Techno', 'techno', 'minimal techno', 'detroit techno', 'acid techno'],
    ['Downtempo', 'downtempo', 'chillout', 'midtempo'],
    ['Breaks', 'breaks', 'breakbeat'],
    ['Jazz', 'jazz'],
    ['Soul', 'soul'],
    ['Funk', 'funk'],
    ['Disco', 'disco'],
    ['Ambient', 'ambient'],
    ['Blues', 'blues'],
    ['Reggae', 'reggae'],
  ] as [string, ...string[]][]).flatMap(([canonical, ...variants]) =>
    variants.map(v => [v, canonical])
  )
);

/**
 * Normalize a genre string to its canonical form.
 * Returns the canonical genre name or a title-cased version of the input.
 */
export function normalizeGenre(genre?: string): string {
  const tagged = taggedGenre(genre);
  if (!tagged) return 'Other';
  const key = tagged.trim().toLowerCase();

  // Check alias map
  if (GENRE_ALIASES[key]) {
    return GENRE_ALIASES[key];
  }

  // Check if it already matches a canonical genre (case-insensitive)
  const canonical = CANONICAL_GENRES.find(g => g.toLowerCase() === key);
  if (canonical) return canonical;

  // Title-case the original
  return tagged.trim().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Extract unique normalized genres from a list of streams, sorted with
 * canonical genres first in their defined order, then others alphabetically.
 */
export function getUniqueGenres(streams: { genre?: string }[]): string[] {
  const normalized = new Set<string>();
  streams.forEach(s => {
    if (taggedGenre(s.genre)) {
      normalized.add(normalizeGenre(s.genre));
    }
  });

  const canonicalList: string[] = [];
  const other: string[] = [];

  normalized.forEach(genre => {
    if ((CANONICAL_GENRES as readonly string[]).includes(genre)) {
      canonicalList.push(genre);
    } else {
      other.push(genre);
    }
  });

  // Sort canonical by defined order, others alphabetically
  canonicalList.sort((a, b) =>
    (CANONICAL_GENRES as readonly string[]).indexOf(a) - (CANONICAL_GENRES as readonly string[]).indexOf(b)
  );
  other.sort();

  return [...canonicalList, ...other];
}
